/**
 * AudioVisualizer Component
 * 
 * Provides real-time visualization of audio passing through the application.
 * Displays both a waveform and frequency spectrum visualization.
 */

import React, { useEffect, useRef, useState } from 'react';
import './styles.scss';

interface AudioVisualizerProps {
  audioContext?: AudioContext;
  /** Whether the visualization is currently active */
  active?: boolean;
  /** The type of visualization to display */
  type?: 'waveform' | 'frequency' | 'both';
  /** The height of the visualization in pixels */
  height?: number;
  /** Additional class names to apply to the component */
  className?: string;
}

const AudioVisualizer: React.FC<AudioVisualizerProps> = ({
  audioContext,
  active = false,
  type = 'both',
  height = 100,
  className = '',
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const analyzerRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const animationRef = useRef<number | null>(null);
  const [isAudioDetected, setIsAudioDetected] = useState(false);
  const [averageVolume, setAverageVolume] = useState(0);

  // Define draw functions outside useEffect but wrap in useCallback to memoize them
  const drawWaveform = React.useCallback(
    (
      ctx: CanvasRenderingContext2D,
      analyzer: AnalyserNode,
      width: number,
      height: number
    ) => {
      const bufferLength = analyzer.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      analyzer.getByteTimeDomainData(dataArray);

      const sliceHeight = type === 'both' ? height / 2 : height;
      const yOffset = type === 'both' ? 0 : 0;

      ctx.lineWidth = 2;
      ctx.strokeStyle = isAudioDetected ? '#5ee0ff' : '#3a5259';
      ctx.beginPath();

      const sliceWidth = width / bufferLength;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const y = v * sliceHeight / 2 + yOffset;

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }

        x += sliceWidth;
      }

      ctx.lineTo(width, sliceHeight / 2 + yOffset);
      ctx.stroke();
    },
    [type, isAudioDetected]
  );

  // Draw frequency spectrum visualization
  const drawFrequency = React.useCallback(
    (
      ctx: CanvasRenderingContext2D,
      analyzer: AnalyserNode,
      width: number,
      height: number
    ) => {
      const bufferLength = analyzer.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      analyzer.getByteFrequencyData(dataArray);

      const sliceHeight = type === 'both' ? height / 2 : height;
      const yOffset = type === 'both' ? sliceHeight : 0;

      const barWidth = width / (bufferLength / 4);
      let x = 0;

      // Only visualize the lower frequencies (more relevant for speech)
      const usableLength = bufferLength / 4;

      for (let i = 0; i < usableLength; i++) {
        const barHeight = (dataArray[i] / 255) * sliceHeight;

        // Color gradient based on frequency and volume
        const hue = i / usableLength * 180 + 180;
        const saturation = 80;
        const lightness = 50;
        
        ctx.fillStyle = isAudioDetected 
          ? `hsla(${hue}, ${saturation}%, ${lightness}%, 0.8)` 
          : `hsla(200, 30%, 20%, 0.5)`;
          
        ctx.fillRect(x, height - barHeight - yOffset + sliceHeight, barWidth - 1, barHeight);

        x += barWidth;
      }
    },
    [type, isAudioDetected]
  );

  useEffect(() => {
    if (!active || !audioContext) {
      // Stop visualizer if inactive
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      return;
    }

    // Initialize audio analyzer if it doesn't exist
    if (!analyzerRef.current) {
      const analyzer = audioContext.createAnalyser();
      analyzer.fftSize = 2048;
      analyzer.smoothingTimeConstant = 0.8;
      analyzerRef.current = analyzer;

      // Get the existing destination (speakers) node
      const destination = audioContext.destination;

      // Connect analyzer as a parallel path to the destination
      // This way, the audio still plays through the speakers but we can also analyze it
      if (audioContext.state === 'running') {
        analyzer.connect(destination);
      }
    }

    // Try to get microphone access for direct input visualization
    const getMicrophoneInput = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyzerRef.current!);
        sourceRef.current = source;
      } catch (error) {
        console.warn('Could not access microphone for visualization:', error);
        // If microphone access fails, we'll still visualize output audio
      }
    };

    // Only request microphone if we're active
    if (active) {
      getMicrophoneInput();
    }

    // Setup canvas and drawing context
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = canvas.clientWidth * window.devicePixelRatio;
    canvas.height = height * window.devicePixelRatio;

    const analyzer = analyzerRef.current;
    const bufferLength = analyzer.frequencyBinCount;
    const volumeDataArray = new Uint8Array(bufferLength);

    // Draw visualization frame
    const draw = () => {
      if (!analyzer) return;

      animationRef.current = requestAnimationFrame(draw);
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // For volume detection
      analyzer.getByteTimeDomainData(volumeDataArray);
      let sum = 0;
      for (let i = 0; i < volumeDataArray.length; i++) {
        const amplitude = (volumeDataArray[i] - 128) / 128;
        sum += Math.abs(amplitude);
      }
      const averageVol = sum / volumeDataArray.length;
      setAverageVolume(averageVol);
      
      // Detect if audio is active (if volume exceeds threshold)
      setIsAudioDetected(averageVol > 0.01);

      // Select visualization type
      if (type === 'waveform' || type === 'both') {
        drawWaveform(ctx, analyzer, canvas.width, canvas.height);
      }
      
      if (type === 'frequency' || type === 'both') {
        drawFrequency(ctx, analyzer, canvas.width, canvas.height);
      }
    };

    // Start visualization
    draw();

    // Cleanup
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
    };
  }, [active, audioContext, height, type, drawWaveform, drawFrequency]);



  return (
    <div className={`audio-visualizer ${className} ${isAudioDetected ? 'active' : 'inactive'}`}>
      <canvas 
        ref={canvasRef} 
        height={height} 
        className="visualizer-canvas"
      />
      {isAudioDetected && (
        <div className="audio-indicator">
          <div className="pulse-dot" style={{ opacity: Math.min(1, averageVolume * 10) }}></div>
          <span>Audio Active</span>
        </div>
      )}
    </div>
  );
};

export default AudioVisualizer;
